import { z } from 'zod';
import { EMBEDDED_LIMITS, MEDIA_VARIANTS, PAGINATION, VIEW_COUNTER_ENTITIES, } from '../../constants/index.js';
import { defaulted, mediaRefSchema, objectIdSchema, requiredTranslatable, seoMetaSchema, translatableString, } from '../common.js';
import { auditableSchema } from './base.js';
// ─── Menyu ───────────────────────────────────────────────────────────────────
export const MENU_LOCATIONS = ['header', 'footer', 'footer-secondary'];
export const menuLinkTypeSchema = z.enum([
    'page',
    'news',
    'service',
    'product',
    'category',
    'project',
    'course',
    'external',
    'none',
]);
/** 2 daraja bilan cheklangan (EMBEDDED_LIMITS.MENU_DEPTH). */
const menuItemBase = z.object({
    _id: z.string().min(1).max(40),
    label: requiredTranslatable({ max: 200 }),
    linkType: menuLinkTypeSchema.default('none'),
    targetId: objectIdSchema.nullable().default(null),
    url: z.string().trim().max(300).nullable().default(null),
    target: z.enum(['_self', '_blank']).default('_self'),
    icon: z.string().trim().max(40).nullable().default(null),
    order: z.number().int().min(0).default(0),
    isVisible: z.boolean().default(true),
});
export const menuItemSchema = menuItemBase.extend({
    children: z.array(menuItemBase).max(EMBEDDED_LIMITS.MENU_ITEMS).default([]),
});
export const menuSchema = auditableSchema.omit({ deletedAt: true }).extend({
    location: z.enum(MENU_LOCATIONS),
    items: z.array(menuItemSchema).max(EMBEDDED_LIMITS.MENU_ITEMS).default([]),
});
// ─── Sozlamalar (singleton) ──────────────────────────────────────────────────
export const SOCIAL_TYPES = [
    'facebook',
    'instagram',
    'telegram',
    'youtube',
    'whatsapp',
    'wechat',
    'linkedin',
    'x',
];
export const settingsSchema = z.object({
    _id: z.literal('global'),
    siteName: requiredTranslatable({ max: 200 }),
    siteDescription: translatableString({ max: 600 }),
    logo: mediaRefSchema.nullable().default(null),
    logoLight: mediaRefSchema.nullable().default(null),
    favicon: mediaRefSchema.nullable().default(null),
    contacts: defaulted(z.object({
        phones: z
            .array(z.object({ label: z.string().max(100).default(''), value: z.string().max(40) }))
            .max(10)
            .default([]),
        emails: z
            .array(z.object({ label: z.string().max(100).default(''), value: z.string().max(200) }))
            .max(10)
            .default([]),
        address: translatableString({ max: 500 }),
        workingHours: translatableString({ max: 300 }),
        mapLat: z.number().min(-90).max(90).nullable().default(null),
        mapLng: z.number().min(-180).max(180).nullable().default(null),
        mapZoom: z.number().int().min(1).max(20).default(16),
        yandexMapsApiKey: z.string().max(200).nullable().default(null),
    })),
    socials: z
        .array(z.object({
        type: z.enum(SOCIAL_TYPES),
        url: z.string().trim().max(300),
        isVisible: z.boolean().default(true),
    }))
        .max(20)
        .default([]),
    /**
     * Dron-shou sahifasining ko'rsatkichlari va tavsif kartochkalari
     * (maket `C5 Dron-shou.dc.html`).
     *
     * Nega SOZLAMALARDA: bular butun sahifaga tegishli marketing
     * raqamlari («1300+ yorug'lik droni»), bitta paketga emas. Kod ichida
     * qattiq yozilsa, mijoz flot o'sganda dasturchiga murojaat qilardi.
     */
    droneShow: defaulted(z.object({
        stats: z
            .array(z.object({
            value: z.string().trim().max(40),
            label: translatableString({ max: 200 }),
        }))
            .max(8)
            .default([]),
        features: z
            .array(z.object({
            title: translatableString({ max: 200 }),
            body: translatableString({ max: 800 }),
        }))
            .max(8)
            .default([]),
    })),
    shop: defaulted(z.object({
        currency: z.literal('UZS').default('UZS'),
        cartEnabled: z.boolean().default(true),
        deliveryRegions: z
            .array(translatableString({ max: 200 }))
            .max(30)
            .default([]),
        requestPrefix: z
            .string()
            .regex(/^[A-Z]{2,6}$/)
            .default('GEO'),
        perPage: z.number().int().min(6).max(60).default(20),
    })),
    /**
     * `botToken` bazada AES-256-GCM bilan SHIFRLANGAN holda saqlanadi (TZ 11.3).
     * Public mapper'dan hech qachon o'tmaydi.
     */
    telegram: defaulted(z.object({
        botToken: z.string().max(500).default(''),
        isEnabled: z.boolean().default(false),
        /** Har bir ariza turi o'z guruhiga/mavzusiga yo'naltiriladi (TZ 5.4) */
        routes: z
            .array(z.object({
            requestType: z.string().max(30),
            chatId: z.string().max(50),
            threadId: z.string().max(50).nullable().default(null),
        }))
            .max(20)
            .default([]),
    })),
    recaptcha: defaulted(z.object({
        siteKey: z.string().max(200).default(''),
        secretKey: z.string().max(500).default(''),
        isEnabled: z.boolean().default(false),
        minScore: z.number().min(0).max(1).default(0.5),
    })),
    seo: defaulted(z.object({
        defaultMeta: defaulted(seoMetaSchema),
        googleSiteVerification: z.string().max(200).nullable().default(null),
        yandexVerification: z.string().max(200).nullable().default(null),
        robotsTxtExtra: z.string().max(4000).default(''),
    })),
    maintenance: defaulted(z.object({
        isEnabled: z.boolean().default(false),
        message: translatableString({ max: 1000 }),
    })),
    updatedAt: z.coerce.date(),
    updatedBy: objectIdSchema.nullable().default(null),
});
/** Ommaviy javob — maxfiy maydonlarsiz (docs/03-CONVENTIONS.md §2). */
export const publicSettingsSchema = settingsSchema.omit({ telegram: true }).extend({
    recaptcha: z.object({ siteKey: z.string(), isEnabled: z.boolean() }),
});
// ─── Media ───────────────────────────────────────────────────────────────────
export const mediaVariantsSchema = z.object({
    original: z.string().max(500),
    large: z.string().max(500).nullable().default(null),
    medium: z.string().max(500).nullable().default(null),
    square: z.string().max(500).nullable().default(null),
    thumb: z.string().max(500).nullable().default(null),
    blurDataUrl: z.string().max(4000).nullable().default(null),
});
export const mediaSchema = auditableSchema.extend({
    filename: z.string().max(200),
    originalName: z.string().max(400),
    mimeType: z.string().max(120),
    size: z.number().int().nonnegative(),
    width: z.number().int().nonnegative().nullable().default(null),
    height: z.number().int().nonnegative().nullable().default(null),
    folderId: objectIdSchema.nullable().default(null),
    alt: translatableString({ max: 500 }),
    variants: mediaVariantsSchema,
    source: z.enum(['upload', 'migration', 'request']).default('upload'),
});
export const mediaFolderSchema = auditableSchema.extend({
    name: z.string().trim().min(1).max(200),
    parentId: objectIdSchema.nullable().default(null),
});
/**
 * Media qayerda ishlatilgani — ALOHIDA kolleksiya (ISH-REJA §4.3).
 * Hujjat ichidagi massiv bo'lsa, bitta logotip 500 joyda ishlatilganda shishadi.
 */
export const mediaUsageSchema = z.object({
    _id: objectIdSchema,
    mediaId: objectIdSchema,
    collectionName: z.string().max(60),
    docId: objectIdSchema,
    field: z.string().max(120),
    createdAt: z.coerce.date(),
});
// ─── Media so'rovlari ────────────────────────────────────────────────────────
export const listMediaQuerySchema = z.object({
    page: z.coerce.number().int().min(1).max(PAGINATION.MAX_PAGE).default(1),
    limit: z.coerce.number().int().min(1).max(PAGINATION.MAX_LIMIT).default(PAGINATION.DEFAULT_LIMIT),
    folderId: z.union([objectIdSchema, z.literal('root')]).optional(),
    /** `image` — rasm variantlari bor fayllar, `file` — PDF va shu kabilar. */
    kind: z.enum(['image', 'file']).optional(),
    search: z.string().trim().max(200).optional(),
});
export const updateMediaSchema = z.object({
    alt: translatableString({ max: 500 }).optional(),
    folderId: objectIdSchema.nullable().optional(),
});
export const uploadMediaMetaSchema = z.object({
    folderId: objectIdSchema.nullable().optional(),
});
export const createMediaFolderSchema = z.object({
    name: z.string().trim().min(1).max(200),
    parentId: objectIdSchema.nullable().default(null),
});
// ─── Audit jurnali ───────────────────────────────────────────────────────────
export const AUDIT_ACTIONS = [
    'create',
    'update',
    'delete',
    'publish',
    'unpublish',
    'login',
    'login_failed',
    'settings_change',
    'price_change',
    'export',
    'migration',
];
export const listAuditQuerySchema = z.object({
    page: z.coerce.number().int().min(1).max(PAGINATION.MAX_PAGE).default(1),
    limit: z.coerce.number().int().min(1).max(PAGINATION.MAX_LIMIT).default(PAGINATION.DEFAULT_LIMIT),
    userId: objectIdSchema.optional(),
    action: z.enum(AUDIT_ACTIONS).optional(),
    entityType: z.string().trim().max(60).optional(),
    from: z.coerce.date().optional(),
    to: z.coerce.date().optional(),
});
export const auditLogSchema = z.object({
    _id: objectIdSchema,
    userId: objectIdSchema.nullable().default(null),
    userEmail: z.string().max(200).default(''),
    action: z.enum(AUDIT_ACTIONS),
    entityType: z.string().max(60),
    entityId: objectIdSchema.nullable().default(null),
    changes: z
        .record(z.string(), z.object({ from: z.unknown(), to: z.unknown() }))
        .nullable()
        .default(null),
    ip: z.string().max(64).default(''),
    userAgent: z.string().max(500).default(''),
    createdAt: z.coerce.date(),
    /** TTL indeks shu maydon bo'yicha — 365 kun */
    expiresAt: z.coerce.date(),
});
// ─── Ko'rishlar hisoblagichi ────────────────────────────────────────────────
/**
 * Kunlik bucket (ISH-REJA §4.2).
 *
 * Nima uchun alohida: `product.viewCount` ni har ko'rishda `$inc` qilish
 * `updatedAt` ni o'zgartiradi → ISR keshi bekor bo'ladi → sayt sekinlashadi.
 * Bu yerda asosiy hujjatga umuman tegilmaydi.
 *
 * `_id` deterministik: `product:<id>:2026-08-07` — upsert poyga holatisiz ishlaydi.
 */
export const viewCounterSchema = z.object({
    _id: z.string().max(80),
    entityType: z.enum(VIEW_COUNTER_ENTITIES),
    entityId: objectIdSchema,
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    count: z.number().int().min(0).default(0),
    expiresAt: z.coerce.date(),
});
export function viewCounterId(entityType, entityId, date) {
    return `${entityType}:${entityId}:${date}`;
}
export const MEDIA_VARIANT_NAMES = Object.keys(MEDIA_VARIANTS);
//# sourceMappingURL=system.js.map